/* eslint-disable @next/next/no-img-element */

"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDictionary } from "@/lib/useDictionary";


interface VideoShowcaseProps {
  videoUrl?: string;
  thumbnailUrl?: string;
  title?: string;
  className?: string;
}

export default function VideoShowcase({
  videoUrl = "https://www.youtube.com/embed/0bBqL_HHNp0",
  thumbnailUrl = "/banner/Youtube.png",
  title,
  className,
}: VideoShowcaseProps) {
  const dict = useDictionary();
  const [isPlaying, setIsPlaying] = useState(false);
  const resolvedTitle = title ?? dict.video.defaultTitle;

  return (
    <section
      className={cn(
        "relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(125,211,252,0.28),_transparent_28%),linear-gradient(180deg,#eef6ff_0%,#dcecff_48%,#eef6ff_100%)] py-20",
        className,
      )}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--blue-900)] mb-4">
            {dict.video.heading}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {dict.video.subHeading}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl"
        >
          {isPlaying ? (
            <>
              <iframe
                src={`${videoUrl}?autoplay=1&mute=1&rel=0&modestbranding=1`}
                title={resolvedTitle}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
              {/* Close Button */}
              <button
                onClick={() => setIsPlaying(false)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              {/* Thumbnail / Placeholder */}
              <div className="absolute inset-0 bg-gradient-to-br from-[var(--blue-700)] to-[var(--blue-900)]">
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt={resolvedTitle}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  /* Decorative Background */
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full max-w-3xl mx-auto px-8">
                      {/* Mock Dashboard Preview */}
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                        <div className="flex gap-2 mb-4">
                          <div className="w-3 h-3 rounded-full bg-red-400" />
                          <div className="w-3 h-3 rounded-full bg-yellow-400" />
                          <div className="w-3 h-3 rounded-full bg-green-400" />
                        </div>
                        <div className="space-y-4">
                          <div className="flex gap-4">
                            <div className="flex-1 h-20 bg-white/20 rounded-lg" />
                            <div className="flex-1 h-20 bg-white/20 rounded-lg" />
                            <div className="flex-1 h-20 bg-white/20 rounded-lg" />
                          </div>
                          <div className="h-32 bg-white/20 rounded-lg" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Play Button */}
              <button
                onClick={() => setIsPlaying(true)}
                className="absolute inset-0 flex items-center justify-center group"
              >
                <motion.div
                  initial={{ scale: 1 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative"
                >
                  {/* Pulse Animation */}
                  <div className="absolute inset-0 w-24 h-24 gradient-accent rounded-full animate-ping opacity-30" />
                  {/* Button */}
                  <div className="relative w-24 h-24 gradient-accent rounded-full flex items-center justify-center shadow-2xl">
                    <Play className="w-10 h-10 text-white ml-1" />
                  </div>
                </motion.div>
              </button>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
}
